// src/utils/sheetStatus.js
// --------------------------------------------------------------------------
// Fuente ÚNICA de verdad para el "Estado" de una plancha en Prepress.
//
// Regla central (definida con negocio, 16-ago):
//
//   El ESTADO es EXCLUYENTE y sale SOLO de `sheet.status`. Son tres:
//        · "Listo para Prepress"   → lo que viene de diseño
//        · "Listo para Producción" → lo que está por imprimir/cortar
//        · "Devuelto a Diseño"     → tal cual
//   Más "En Hold" como cuarta opción del filtro.
//
//   `productionStatus` es AVANCE DE PRODUCCIÓN, no estado, y NO se lee acá. Se
//   muestra en su propia columna (computeProductionStatus, lib/production.js).
//   Leerlo acá tapaba el estado real en 939 planchas: se escribe en cuanto alguien
//   marca una repetición y NUNCA se limpia. Entró en 46164b3.
//
//   Si la plancha tiene una ALERTA activa (tarea de sistema pendiente), ese estado
//   MANDA por encima de todo (prioridad 0):
//        · tarea type 'system'               -> "Devuelto a Diseño"
//        · tarea type 'system_reproduction'  -> "Reproducir"
//        · cualquier otra tarea de sistema   -> "En Hold"
//
//   "Actualizada" NO es un estado: es un EVENTO ortogonal. Una plancha puede estar
//   lista para producción Y haber sido actualizada. Ver isSheetUpdated, abajo.
// --------------------------------------------------------------------------

export const STATUS_LISTO_PREPRESS = 'Listo para Prepress';
export const STATUS_LISTO_PRODUCCION = 'Listo para Producción';
export const STATUS_DEVUELTO_DISENO = 'Devuelto a Diseño';
export const STATUS_REPRODUCIR = 'Reproducir';
export const STATUS_HOLD = 'En Hold';

// Las cuatro opciones EXCLUYENTES del filtro de estado, en orden de aparición.
// "Reproducir" queda fuera a propósito: se deriva de una tarea de alerta, no de
// `sheet.status`, y solo aparece bajo "Todos los Estados" (igual que antes).
export const SHEET_STATUS_OPTIONS = [
    STATUS_LISTO_PREPRESS,
    STATUS_LISTO_PRODUCCION,
    STATUS_DEVUELTO_DISENO,
    STATUS_HOLD,
];

// Tipos de tarea que se consideran "alerta" (pintan la fila roja parpadeante).
export const ALERT_TASK_TYPES = ['system', 'system_reproduction'];

// ¿La tarea es una alerta activa (pendiente y de tipo de sistema)?
export const isActiveAlertTask = (task) =>
    !!task && task.status !== 'completed' && ALERT_TASK_TYPES.includes(task.type);

// Dado el tipo de tarea de alerta, devuelve el estado que debe mostrar la plancha.
export const statusFromAlertTask = (task) => {
    if (!task) return null;
    if (task.type === 'system') return STATUS_DEVUELTO_DISENO;
    if (task.type === 'system_reproduction') return STATUS_REPRODUCIR;
    return STATUS_HOLD;
};

// Construye un Map sheetId -> tarea de alerta pendiente (la más reciente gana).
// Se usa en la tabla para saber qué plancha está en alerta y de qué tipo.
export const buildAlertTaskBySheet = (tasks = []) => {
    const map = new Map();
    (tasks || []).forEach((t) => {
        if (isActiveAlertTask(t) && t.sheetId) {
            map.set(t.sheetId, t);
        }
    });
    return map;
};

// Normalización del `sheet.status` CRUDO (legados incluidos) al estado a mostrar.
//
// 'Corregido por Diseño' cae en "Listo para Prepress": una plancha corregida ES lo
// que viene de diseño. Su "corregidez" no es un estado — la marca isCorregidaPorDiseno,
// que conserva la señal visual sin competir con los tres excluyentes.
//
// Los legados salen del comentario de StatusBadge en PrepressTab:
// 'En Ruta' ≡ 'Listo para Prepress' y 'Devuelto a Revisión' ≡ 'Devuelto a Diseño'.
const STATUS_ALIASES = {
    'Listo para Prepress': STATUS_LISTO_PREPRESS,
    'Pendiente': STATUS_LISTO_PREPRESS,
    'En Ruta': STATUS_LISTO_PREPRESS,               // legacy ≡ Listo para Prepress
    'Corregido por Diseño': STATUS_LISTO_PREPRESS,
    'Listo para Producción': STATUS_LISTO_PRODUCCION,
    'Devuelto a Diseño': STATUS_DEVUELTO_DISENO,
    'Devuelto a Revisión': STATUS_DEVUELTO_DISENO,  // legacy ≡ Devuelto a Diseño
    'Hold': STATUS_HOLD,                            // legacy del SheetModal
    'En Hold': STATUS_HOLD,
    'Reproducir': STATUS_REPRODUCIR,                // 0 planchas hoy; el vivo sale de la alerta
};

/**
 * Valores de `sheet.status` que llegan a la tabla de Prepress.
 * Se DERIVA de STATUS_ALIASES a propósito: así ningún estado puede quedar
 * mostrable-pero-invisible (era el caso de las 4 planchas en 'Hold', que
 * getSheetDisplayStatus sabía normalizar pero la lista blanca dejaba fuera).
 */
export const PREPRESS_STATUS_WHITELIST = Object.keys(STATUS_ALIASES);

/**
 * Estado a mostrar para una plancha. Si se pasa una tarea de alerta activa,
 * ese estado tiene prioridad absoluta.
 * @param {object} sheet
 * @param {object|null} alertTask
 * @return {string}
 */
export const getSheetDisplayStatus = (sheet, alertTask = null) => {
    if (!sheet) return STATUS_LISTO_PREPRESS;

    // Prioridad 0: ALERTA activa manda sobre todo.
    const alertStatus = statusFromAlertTask(alertTask);
    if (alertStatus) return alertStatus;

    // El estado sale SOLO de sheet.status. productionStatus NO participa.
    return STATUS_ALIASES[sheet.status] || STATUS_LISTO_PREPRESS;
};

/**
 * ¿Volvió corregida de diseño? Lee el status CRUDO, no el derivado: el display la
 * agrupa en "Listo para Prepress" (es lo que viene de diseño), pero la señal visual
 * —fila morada + chip "↻ Corregida"— se conserva. Se apaga sola cuando el status
 * cambia, exactamente como antes: no hay campo nuevo ni migración.
 */
export const isCorregidaPorDiseno = (sheet) => sheet?.status === 'Corregido por Diseño';

// --------------------------------------------------------------------------
// "Actualizada": EVENTO, no estado.
//
// Match EXACTO y case-sensitive contra la lista. El matcher anterior usaba
// `.includes('actualizada')` en minúscula, que:
//   · NO matcheaba 'Plancha Actualizada' (la acción real, con mayúscula), y
//   · SÍ matcheaba 'Nota de Prepress actualizada' (editar una nota, que no es
//     actualizar la plancha).
// Medido en producción 16-ago: el filtro devolvía 242 planchas, 241 de ellas
// falsos positivos por nota, y las 539 con el evento real nunca aparecían.
//
// Quedan fuera A PROPÓSITO las acciones de prepress trabajando —no son
// actualizaciones de la plancha—: 'Nota de Prepress actualizada',
// 'Archivo de producción subido: …' y 'Archivos de producción completos. Estado
// cambiado a "…"' (esta última son 1914 entradas: contarla ahogaría la señal).
//
// DEUDA CONOCIDA: esto sigue siendo comparación de texto libre en español. Un
// cambio de copy en SheetModal lo rompe en silencio, que es la misma clase de
// fallo que causó el bug original. La corrección de fondo (un `type` propio en la
// entrada de historial, en vez de un string) va con el ítem 8 del handoff.
// --------------------------------------------------------------------------

/** Acciones de `history[]` que cuentan como actualización de la plancha. */
export const UPDATE_ACTIONS = ['Plancha Actualizada', 'Plancha Corregida'];

/** ¿La plancha tiene alguna actualización registrada en su historial? */
export const isSheetUpdated = (sheet) =>
    (sheet?.history || []).some((h) => UPDATE_ACTIONS.includes(h?.action));
